import { useSheetManager } from '@/lib/ui';
import { useBrowserNavigation } from '@/lib/ui/BrowserNavigationContext';
import type { SheetNavigationList, SheetNavigationItem } from '@/lib/ui/SheetManager';
import { InitiativeDetail } from '@/components/initiatives/InitiativeDetail';
import { ApiInitiative } from '@/lib/api';
import { useInitiative } from './useInitiatives';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { initiativeQueryKeys } from './useInitiatives';

interface ShowInitiativeDetailOptions {
  showParticipationAction?: boolean;
  keepOpenOnRouteChange?: boolean;
  /** Navigation list for prev/next browsing through initiatives. When omitted, falls back to browser context. */
  navigationList?: SheetNavigationList;
}

export function useInitiativeDetailSheet() {
  const { openSheet } = useSheetManager();
  const { initiativeNavigationList } = useBrowserNavigation();
  const queryClient = useQueryClient();

  const showInitiativeDetail = (
    initiativeIdOrData: number | ApiInitiative,
    options?: ShowInitiativeDetailOptions
  ) => {
    let initiativeId: number;
    let initiativeTitle = 'Initiative Detail';

    if (typeof initiativeIdOrData === 'object') {
      // Seed the query cache so the loader renders instantly
      initiativeId = initiativeIdOrData.id;
      initiativeTitle = initiativeIdOrData.name || initiativeTitle;
      queryClient.setQueryData(initiativeQueryKeys.detail(initiativeId), initiativeIdOrData);
    } else {
      initiativeId = initiativeIdOrData;
      // Try to get the name from the query cache
      const cached = queryClient.getQueryData<ApiInitiative>(
        initiativeQueryKeys.detail(initiativeId)
      );
      if (cached?.name) {
        initiativeTitle = cached.name;
      }
    }

    const navList = options?.navigationList ?? initiativeNavigationList;
    openSheet({
      id: `initiative-detail-${initiativeId}`,
      title: initiativeTitle,
      content: <InitiativeDetailLoader initiativeId={initiativeId} options={options} />,
      maxWidth: 'sm:max-w-4xl',
      keepOpenOnRouteChange: options?.keepOpenOnRouteChange ?? false,
      navigationList: navList,
      currentNavigationId: navList ? String(initiativeId) : undefined,
    });
  };

  return { showInitiativeDetail };
}

// Helper component to load initiative data when only ID is provided
function InitiativeDetailLoader({
  initiativeId,
  options,
}: {
  initiativeId: number;
  options?: ShowInitiativeDetailOptions;
}) {
  const { data: initiative, isLoading } = useInitiative(initiativeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!initiative) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Initiative not found</p>
      </div>
    );
  }

  return (
    <InitiativeDetail
      initiative={initiative}
      showParticipationAction={options?.showParticipationAction ?? false}
    />
  );
}

/** Helper to build a navigation list for initiatives */
export function buildInitiativeNavigationList(
  initiatives: ApiInitiative[],
  options?: { showParticipationAction?: boolean }
): SheetNavigationList {
  return {
    items: initiatives.map((i) => ({
      id: String(i.id),
      title: i.name || 'Initiative Detail',
    })),
    renderContent: (item: SheetNavigationItem) => (
      <InitiativeDetailLoader initiativeId={Number(item.id)} options={options} />
    ),
    maxWidth: 'sm:max-w-4xl',
  };
}
